import {
  DOCUMENT_ID_FIELD,
  getCantileverDocumentId,
  getSystemDocumentBqId,
  PIPELINE_CONFIG_HASH_FIELD,
  type SystemDocumentEntry,
} from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { RolodexEntryType, RolodexFilterOp, RolodexFilterType } from 'types'

type ScopedTags = Record<string, string | string[]>

export type SystemDocumentLoadOptions = {
  type: RolodexEntryType[]
  tags: ScopedTags
  ids?: string[]
  filters?: RolodexFilterType[]
  limit?: number
}

export type SystemDocumentPageLoadOptions = {
  type: RolodexEntryType[]
  tags: ScopedTags
  filters?: RolodexFilterType[]
  limit?: number
}

function documentIdFilter(
  documentIds: string | string[],
  op: RolodexFilterOp = RolodexFilterOp.EQUAL
): RolodexFilterType {
  return {
    field: 'data',
    key: DOCUMENT_ID_FIELD,
    op: Array.isArray(documentIds) ? RolodexFilterOp.IN : op,
    value: documentIds,
  }
}

function pipelineConfigHashFilter(
  pipelineConfigHash: string
): RolodexFilterType {
  return {
    field: 'data',
    key: PIPELINE_CONFIG_HASH_FIELD,
    op: RolodexFilterOp.EQUAL,
    value: pipelineConfigHash,
  }
}

/** Load a system-document by Rolodex entry id when the UUID is already known. */
export function systemDocumentByEntryId(
  entryId: string,
  scopedTags: ScopedTags
): SystemDocumentLoadOptions {
  return {
    type: [RolodexEntryType.SYSTEM_DOCUMENT],
    ids: [entryId],
    tags: scopedTags,
    limit: 1,
  }
}

/**
 * Load system-document(s) by stable BigQuery document_id field.
 * Config scope comes from pipeline-config tags; optionally also filter hash.
 */
export function systemDocumentByDocumentId(
  documentId: string,
  scopedTags: ScopedTags,
  pipelineConfigHash?: string
): SystemDocumentLoadOptions {
  const filters: RolodexFilterType[] = [documentIdFilter(documentId)]
  if (pipelineConfigHash) {
    filters.push(pipelineConfigHashFilter(pipelineConfigHash))
  }
  return {
    type: [RolodexEntryType.SYSTEM_DOCUMENT],
    tags: scopedTags,
    filters,
    limit: 1,
  }
}

/** Batch-load system-documents by BQ document_id values (e.g. search hydration). */
export function systemDocumentsByDocumentIds(
  documentIds: string[],
  scopedTags: ScopedTags,
  limit = 200
): SystemDocumentLoadOptions | undefined {
  if (documentIds.length === 0) {
    return undefined
  }
  return {
    type: [RolodexEntryType.SYSTEM_DOCUMENT],
    tags: scopedTags,
    filters: [documentIdFilter(documentIds)],
    limit,
  }
}

/** Load all system-document-page entries tagged to a system-document entry. */
export function systemDocumentPagesForDocument(
  systemDocumentEntryId: string,
  scopedTags: ScopedTags,
  limit = 10000
): SystemDocumentPageLoadOptions {
  return {
    type: [RolodexEntryType.SYSTEM_DOCUMENT_PAGE],
    tags: {
      ...scopedTags,
      'system-document': systemDocumentEntryId,
    },
    limit,
  }
}

/** Load a single system-document-page for a page number under a system-document. */
export function systemDocumentPageForPageNumber(
  systemDocumentEntryId: string,
  pageNumber: number,
  scopedTags: ScopedTags
): SystemDocumentPageLoadOptions {
  return {
    type: [RolodexEntryType.SYSTEM_DOCUMENT_PAGE],
    tags: {
      ...scopedTags,
      'system-document': systemDocumentEntryId,
    },
    filters: [
      {
        field: 'data',
        key: 'page_num',
        op: RolodexFilterOp.EQUAL,
        value: pageNumber,
      },
    ],
    limit: 1,
  }
}

/**
 * Build pageNumber → cantilever-document-page entry id from loaded system pages.
 */
export function buildCantileverPageHostByPageNum(
  pages: Array<{
    entry: {
      data: { page_num?: number }
      tags: { [key: string]: string }
    }
  }>
): Map<number, string> {
  const map = new Map<number, string>()
  for (const page of pages) {
    const pageNum = page.entry.data.page_num
    const hostId = page.entry.tags['cantilever-document-page']
    if (
      typeof pageNum === 'number' &&
      typeof hostId === 'string' &&
      hostId.length > 0
    ) {
      map.set(pageNum, hostId)
    }
  }
  return map
}

/**
 * Page-host Rolodex lookup is only needed when a cantilever-document host exists.
 * Legacy docs (no cantilever tag) keep images on `${systemDocumentEntryId}_pg_N`.
 */
export function shouldLookupCantileverPageHost(
  cantileverDocumentId: string | undefined,
  knownCantileverDocumentPageId?: string
): boolean {
  if (knownCantileverDocumentPageId) return false
  return !!cantileverDocumentId
}

function systemDocumentListIdentity(entry: SystemDocumentEntry): string {
  return getSystemDocumentBqId(entry) ?? entry.entry.id
}

function preferSystemDocument(
  current: SystemDocumentEntry,
  candidate: SystemDocumentEntry
): SystemDocumentEntry {
  const currentHasCantilever = getCantileverDocumentId(current) !== undefined
  const candidateHasCantilever =
    getCantileverDocumentId(candidate) !== undefined
  if (candidateHasCantilever && !currentHasCantilever) return candidate
  if (currentHasCantilever && !candidateHasCantilever) return current
  if (candidate.entry.updated_at > current.entry.updated_at) return candidate
  return current
}

/**
 * Collapse multi-config duplicates (site-only loads with no active/preview
 * pipeline-config) to one system-document per stable document identity.
 * Prefers cantilever-tagged hosts, then newest updated_at; preserves first-seen order.
 */
export function dedupeSystemDocumentsByDocumentId(
  entries: SystemDocumentEntry[]
): SystemDocumentEntry[] {
  const preferredByIdentity = new Map<string, SystemDocumentEntry>()
  const order: string[] = []

  for (const entry of entries) {
    const identity = systemDocumentListIdentity(entry)
    const existing = preferredByIdentity.get(identity)
    if (existing === undefined) {
      preferredByIdentity.set(identity, entry)
      order.push(identity)
      continue
    }
    preferredByIdentity.set(identity, preferSystemDocument(existing, entry))
  }

  return order.flatMap((identity) => {
    const entry = preferredByIdentity.get(identity)
    return entry ? [entry] : []
  })
}
